import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MobileHeader from './MobileHeader';
import {
  Box,
  Heading,
  Text,
  Flex,
  Spinner,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  useToast,
  Divider,
  useColorModeValue,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Avatar,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Checkbox,
  useDisclosure,
  Code,
  Tooltip
} from '@chakra-ui/react';
import { FaCalendarAlt, FaMapMarkerAlt, FaUserFriends, FaSearch, FaEdit, FaCamera, FaAngleLeft, FaEye, FaEnvelope, FaPhone, FaBug, FaPaperPlane, FaUserPlus, FaUserSecret, FaLink, FaCopy } from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';
import './EventDashboard.css';
import SalesBreakdownModal from './SalesBreakdownModal';

const EventDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBgColor = useColorModeValue('blue.500', 'blue.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Add state for mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add user state
  const [user, setUser] = useState(null);
  
  // Add touch event handling similar to AdminDashboardView
  const containerRef = useRef(null);
  
  // Add this useEffect for better scrolling on mobile devices
  useEffect(() => {
    // Fix scrolling on mobile devices, especially iOS
    const fixMobileScrolling = () => {
      const container = containerRef.current;
      if (!container) return;
      
      // Use a simple approach for iOS scrolling
      const touchHandler = (e) => {
        if (e.touches.length > 1) {
          // Allow pinch zoom
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', touchHandler, { passive: false });
      
      return () => {
        document.removeEventListener('touchmove', touchHandler);
      };
    };
    
    fixMobileScrolling();
  }, []);
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      // For development purposes, always set as admin
      setIsAdmin(true);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }
        
        // Set the user from the session
        setUser(session.user);
        
        // In production, you'd check if the user is admin or staff
        // const { data, error } = await supabase
        //   .from('app_users')
        //   .select('is_admin, user_type')
        //   .eq('id', session.user.id)
        //   .single();
        
        // if (error || (!data.is_admin && data.user_type !== 'staff')) {
        //   navigate('/dashboard');
        //   return;
        // }
        
        fetchEventDetails();
        fetchEventUsers();
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast({
          title: 'Authentication Error',
          description: 'Please log in again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/login');
      }
    };
    
    checkAdminStatus();
  }, [eventId, navigate, toast]);
  
  // Fetch reports data when users are loaded
  useEffect(() => {
    if (users.length > 0) {
      fetchReportsData();
    }
  }, [users]);
  
  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setEvent(data);
      } else {
        toast({
          title: 'Event not found',
          description: 'The requested event could not be found.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/dashboard/events');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast({
        title: 'Error loading event',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEventUsers = async () => {
    try {
      // First check if the event_id is in a valid format
      if (!eventId || typeof eventId !== 'string') {
        console.error('Invalid event ID format:', eventId);
        throw new Error(`Invalid event ID: ${eventId}`);
      }
      
      // First try: Get debug info
      if (isAdmin) {
        console.log('Admin mode: Running diagnostics first');
        const { data: debugData, error: debugError } = await supabase.rpc('debug_event_users', {
          event_uuid: eventId
        });
        
        if (debugError) {
          console.error('❌ Error getting debug data:', debugError);
        } else {
          console.log('📊 Event Debug Report:', debugData);
        }
      }
      
      // Try using the improved admin_get_event_users RPC function to get registered users
      console.log('Fetching event users using admin_get_event_users function...');
      const { data: eventUsers, error: eventUsersError } = await supabase.rpc('admin_get_event_users', {
        event_uuid: eventId
      });
      
      if (eventUsersError) {
        console.error('❌ Error with admin_get_event_users:', eventUsersError);
        // Fall back to original approach only if this fails
      } else if (eventUsers && eventUsers.length > 0) {
        console.log('✅ Successfully retrieved users for this event:', eventUsers.length);
        
        // Enrich with login stats
        const enriched = await enrichUsersWithLoginStats(eventUsers);
        setUsers(enriched);
        setFilteredUsers(enriched);
        return;
      } else {
        console.log('No registered users found for this event.');
      }
      
      // If we get here, we need to fall back to older approaches
      
      // Try using the general admin_get_app_users RPC function first 
      console.log('Falling back to admin_get_app_users function...');
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('admin_get_app_users', {
        event_uuid: eventId
      });
      
      if (rpcError) {
        console.error('❌ Error using admin_get_app_users RPC:', rpcError);
        // Fall back to direct approaches if the RPC function fails
      } else if (rpcUsers && rpcUsers.length > 0) {
        console.log('✅ Retrieved general users via admin_get_app_users:', rpcUsers.length);
        
        // Now we need to filter this list to only those registered for this event
        const { data: eventOrders, error: eventOrdersError } = await supabase
          .from('event_orders')
          .select('user_id')
          .eq('event_id', eventId);
        
        if (eventOrdersError) {
          console.error('❌ Error fetching event orders for filtering:', eventOrdersError);
        } else if (eventOrders && eventOrders.length > 0) {
          console.log('Found orders to filter users:', eventOrders.length);
          
          // Get the user IDs from the orders
          const registeredUserIds = eventOrders.map(order => order.user_id);
          
          // Filter the users list to include only registered users
          const filteredUsersList = rpcUsers.filter(user => 
            registeredUserIds.includes(user.id) || user.event_id === eventId
          );
          
          console.log('Filtered users list to registered users:', filteredUsersList.length);
          
          if (filteredUsersList.length > 0) {
            // Enrich with login stats
            const enriched = await enrichUsersWithLoginStats(filteredUsersList);
            setUsers(enriched);
            setFilteredUsers(enriched);
            return;
          }
        }
        
        // If we couldn't filter properly, use the full list as a fallback
        // Enrich with login stats
        const enriched = await enrichUsersWithLoginStats(rpcUsers);
        setUsers(enriched);
        setFilteredUsers(enriched);
        return;
      }
      
      // Fallback to checking event_orders directly if all else fails
      console.log('📋 Falling back to event_orders table only');
      
      const { data: eventOrders, error: eventOrdersError } = await supabase
        .from('event_orders')
        .select('user_id, answers, status, created_at')
        .eq('event_id', eventId);
      
      if (eventOrdersError) {
        console.error('❌ Error fetching event_orders:', eventOrdersError);
        throw eventOrdersError;
      }
      
      console.log('✅ Found orders in event_orders table:', eventOrders?.length || 0);
      if (eventOrders?.length > 0) {
        console.log('First order data sample:', eventOrders[0]);
      }
      
      if (eventOrders && eventOrders.length > 0) {
        // Get user details for all users registered for this event
        const userIds = eventOrders.map(order => order.user_id);
        console.log('Looking up user details for IDs:', userIds);
        
        const { data: userData, error: userError } = await supabase
          .from('app_users')
          .select('*')
          .in('id', userIds);
        
        if (userError) {
          console.error('❌ Error fetching user details:', userError);
          throw userError;
        }
        
        console.log('✅ Found user details for orders:', userData?.length || 0);
        if (userData?.length > 0) {
          console.log('First user detail sample:', userData[0]);
        } else {
          console.warn('⚠️ User details not found for order user IDs');
        }
        
        // Combine user data with order data
        let usersList = userData.map(user => {
          const order = eventOrders.find(o => o.user_id === user.id);
          return {
            ...user,
            order_status: order?.status || 'pending',
            answers: order?.answers || {},
            order_created_at: order?.created_at || null
          };
        });
        
        // If we found any users with this approach
        if (usersList.length > 0) {
          console.log('✅ Setting users list with real data from event_orders:', usersList.length);
          // Enrich with login stats
          const enriched = await enrichUsersWithLoginStats(usersList);
          setUsers(enriched);
          setFilteredUsers(enriched);
          return;
        }
      }
      
      // No users found with any approach, fall back to mock data
      console.log('⚠️ No users found with any approach, using mock data');
      useMockEventUsers();
      
    } catch (error) {
      console.error('❌ Error fetching event users:', error);
      toast({
        title: 'Error loading users',
        description: `Could not load user data: ${error.message || 'Unknown error'}. Using sample data instead.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data
      useMockEventUsers();
    }
  };
  
  const useMockEventUsers = () => {
    const mockUsers = [
      {
        id: 'mock-user-1',
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-01T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-2',
        first_name: 'Jane',
        last_name: 'Smith',
        email: 'jane.smith@example.com',
        order_status: 'pending',
        user_type: 'customer',
        created_at: '2023-04-02T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-3',
        first_name: 'Michael',
        last_name: 'Johnson',
        email: 'michael.johnson@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-03T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-4',
        first_name: 'Emily',
        last_name: 'Williams',
        email: 'emily.williams@example.com',
        order_status: 'completed',
        user_type: 'customer',
        created_at: '2023-04-04T00:00:00.000Z',
        profile_image: null
      },
      {
        id: 'mock-user-5',
        first_name: 'David',
        last_name: 'Brown',
        email: 'david.brown@example.com',
        order_status: 'pending',
        user_type: 'customer',
        created_at: '2023-04-05T00:00:00.000Z',
        profile_image: null
      }
    ];
    
    setUsers(mockUsers);
    setFilteredUsers(mockUsers);
  };
  
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (!query.trim() && !teamFilter) {
      setFilteredUsers(users);
      return;
    }
    
    const filtered = users.filter(user => {
      const athleteFirst = user.answers?.athlete_first_name?.toLowerCase() || '';
      const athleteLast  = user.answers?.athlete_last_name?.toLowerCase()  || '';
      const teamName     = user.answers?.team_name?.toLowerCase()          || '';
      const parentFirst  = user.first_name.toLowerCase();
      const parentLast   = user.last_name.toLowerCase();
      const email        = user.email?.toLowerCase() || '';
      const matchesQuery = !query.trim() ||
        athleteFirst.includes(query) ||
        athleteLast.includes(query)  ||
        teamName.includes(query)     ||
        parentFirst.includes(query)  ||
        parentLast.includes(query)   ||
        `${parentFirst} ${parentLast}`.includes(query) ||
        email.includes(query);
      const matchesTeam = !teamFilter || teamName === teamFilter.toLowerCase();
      return matchesQuery && matchesTeam;
    });
    
    setFilteredUsers(filtered);
  };
  
  const handleTeamFilter = (e) => {
    const value = e.target.value;
    setTeamFilter(value);

    // Re-run search with new team filter
    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user => {
      const athleteFirst = user.answers?.athlete_first_name?.toLowerCase() || '';
      const athleteLast  = user.answers?.athlete_last_name?.toLowerCase()  || '';
      const teamName     = user.answers?.team_name?.toLowerCase()          || '';
      const parentFirst  = user.first_name.toLowerCase();
      const parentLast   = user.last_name.toLowerCase();
      const email        = user.email?.toLowerCase() || '';
      const matchesQuery = !query.trim() ||
        athleteFirst.includes(query) ||
        athleteLast.includes(query)  ||
        teamName.includes(query)     ||
        parentFirst.includes(query)  ||
        parentLast.includes(query)   ||
        `${parentFirst} ${parentLast}`.includes(query) ||
        email.includes(query);
      const matchesTeam = !value || teamName === value.toLowerCase();
      return matchesQuery && matchesTeam;
    });
    setFilteredUsers(filtered);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateString;
    }
  };
  
  // Navigate to user image upload
  const handleUploadImages = (userId) => {
    navigate(`/dashboard/events/${eventId}/users/${userId}/images`);
  };
  
  // Function to run diagnostics
  const runDiagnostics = async () => {
    try {
      console.log('Running diagnostics for event:', eventId);
      
      // Get debug information
      const { data, error } = await supabase.rpc('debug_event_users', {
        event_uuid: eventId
      });
      
      if (error) throw error;
      
      setDebugData(data);
      onOpen(); // Open the debug modal
      
    } catch (error) {
      console.error('Error running diagnostics:', error);
      toast({
        title: 'Diagnostics Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Add toggle function for mobile menu
  const toggleMobileMenu = () => {
    const newMobileMenuState = !mobileMenuOpen;
    setMobileMenuOpen(newMobileMenuState);
    
    if (newMobileMenuState) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  };
  
  // Add useEffect for cleanup
  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);
  
  // Track which rows have been sent login details
  const [loginSentMap, setLoginSentMap] = useState({});

  // State and disclosure for manual magic link
  const [linkToCopy, setLinkToCopy] = useState('');
  const { isOpen: isLinkOpen, onOpen: openLinkModal, onClose: closeLinkModal } = useDisclosure();
  
  // Helper to flag a row as sent
  const markLoginSent = (rowKey) => {
    setLoginSentMap(prev => ({ ...prev, [rowKey]: true }));
  };
  
  // --------------------  single-user email helper ----------------
  const sendLoginEmail = async (user, rowKey) => {
    try {
      toast({ title: `Sending email to ${user.email}…`, status: 'info', duration: 3000 });

      // Build redirect URL: localhost for dev, www.playmakerpics.com for prod
      const isLocal = window.location.hostname === 'localhost';
      const redirectUrl = isLocal
        ? 'http://localhost:3000/dashboard'
        : 'https://www.playmakerpics.com/dashboard';

      // 1️⃣  generate magic link via edge function
      const { data: linkData, error: linkErr } = await supabase.functions.invoke('generate-link', {
        body: {
          email: user.email,
          redirect: redirectUrl,
        },
      });

      if (linkErr) throw new Error(linkErr.message);

      const magicLink = linkData.link;

      // 2️⃣  Send e-mail via send-email edge function (bcc notifications)
      const { error: mailErr } = await supabase.functions.invoke('send-email', {
        body: {
          to: user.email,
          bcc: ['images@playmakerpics.com'],
          subject: `${event?.name || 'Event'} - Your edited images are ready!`,
          fromName: 'PlayMaker Pics',
          fromEmail: 'notifications@playmakerpics.com',
          emailType: 'magic-link',
          data: { magicLink },
        },
      });

      if (mailErr) throw new Error(mailErr.message);

      toast({ title: 'E-mail sent', description: `Magic link sent to ${user.email}`, status: 'success' });
      if (rowKey) markLoginSent(rowKey);
    } catch (err) {
      console.error('Send email error', err);
      toast({ title: 'Failed to send email', description: err.message, status: 'error' });
    }
  };
  
  // --------------------  single-user SMS helper ----------------
  const sendLoginText = async (user, rowKey) => {
    if (!user.phone) {
      toast({ title: 'No phone number on file', description: `${user.first_name} does not have a phone number saved.`, status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    try {
      toast({ title: `Sending text to ${user.phone}…`, status: 'info', duration: 3000 });

      // Build redirect URL: localhost for dev, www.playmakerpics.com for prod
      const isLocal = window.location.hostname === 'localhost';
      const redirectUrl = isLocal
        ? 'http://localhost:3000/dashboard'
        : 'https://www.playmakerpics.com/dashboard';

      // 1️⃣  Generate magic link via the existing edge function
      const { data: linkData, error: linkErr } = await supabase.functions.invoke('generate-link', {
        body: {
          email: user.email, // the magic-link function is keyed off e-mail
          redirect: redirectUrl,
        },
      });

      if (linkErr) throw new Error(linkErr.message);

      const magicLink = linkData.link;

      // 2️⃣  Send SMS via an edge function (backed by Twilio or any other vendor)
      const { error: smsErr } = await supabase.functions.invoke('send-sms', {
        body: {
          to: user.phone,          // E.164 format preferred e.g. +15558675309
          message: `Your edited images are ready! Tap to view: ${magicLink}`,
        },
      });

      if (smsErr) throw new Error(smsErr.message);

      toast({ title: 'Text message sent', description: `Login link sent to ${user.phone}`, status: 'success' });
      if (rowKey) markLoginSent(rowKey);
    } catch (err) {
      console.error('Send SMS error', err);
      toast({ title: 'Failed to send text', description: err.message, status: 'error' });
    }
  };
  
  // --------------------  manual magic link generator ----------------
  const generateMagicLink = async (user) => {
    try {
      // Build redirect URL: localhost for dev, www.playmakerpics.com for prod
      const isLocal = window.location.hostname === 'localhost';
      const redirectUrl = isLocal
        ? 'http://localhost:3000/dashboard'
        : 'https://www.playmakerpics.com/dashboard';

      const { data: linkData, error: linkErr } = await supabase.functions.invoke('generate-link', {
        body: {
          email: user.email,
          redirect: redirectUrl,
        },
      });

      if (linkErr) throw new Error(linkErr.message);

      const magicLink = linkData.link;
      setLinkToCopy(magicLink);
      openLinkModal();

      // Optionally copy to clipboard immediately
      try {
        await navigator.clipboard.writeText(magicLink);
        toast({ title: 'Link copied to clipboard', status: 'success', duration: 2000 });
      } catch (_) {
        // ignore clipboard errors (e.g., insecure context)
      }
    } catch (err) {
      console.error('Generate link error', err);
      toast({ title: 'Failed to generate link', description: err.message, status: 'error' });
    }
  };
  
  const enrichUsersWithLoginStats = async (userArr) => {
    try {
      const ids = userArr.map(u => u.id || u.user_id || u.app_user_id);
      if (ids.length === 0) return userArr;
      
      // Fetch login stats for the specific users
      const { data: loginData, error: loginError } = await supabase
        .from('user_logins')
        .select('user_id, created_at')
        .in('user_id', ids);
      if (loginError) throw loginError;
      
      // Fetch media day pics count from user_uploads table (matching Gallery tab approach)
      const { data: mediaData, error: mediaError } = await supabase
        .from('user_uploads')
        .select('user_id')
        .eq('status', 'active')
        .in('user_id', ids);
      if (mediaError) console.warn('Media pics query error:', mediaError);
      
      // Fetch pro edits count
      const { data: proEditData, error: proEditError } = await supabase
        .from('exports')
        .select('user_id')
        .in('user_id', ids);
      if (proEditError) console.warn('Pro edits query error:', proEditError);
      
      // Process login statistics
      const loginStatsMap = {};
      loginData.forEach(r => {
        if (!loginStatsMap[r.user_id]) loginStatsMap[r.user_id] = { count: 0, last: r.created_at };
        loginStatsMap[r.user_id].count += 1;
        if (new Date(r.created_at) > new Date(loginStatsMap[r.user_id].last)) loginStatsMap[r.user_id].last = r.created_at;
      });
      
      // Process media pics count
      const mediaPicsMap = {};
      if (mediaData) {
        mediaData.forEach(r => {
          mediaPicsMap[r.user_id] = (mediaPicsMap[r.user_id] || 0) + 1;
        });
      }
      
      // Process pro edits count
      const proEditsMap = {};
      if (proEditData) {
        proEditData.forEach(r => {
          proEditsMap[r.user_id] = (proEditsMap[r.user_id] || 0) + 1;
        });
      }
      
      return userArr.map(u => {
        const uid = u.id || u.user_id || u.app_user_id;
        return { 
          ...u, 
          login_stats: loginStatsMap[uid] || { count: 0, last: null },
          media_pics_count: mediaPicsMap[uid] || 0,
          pro_edits_count: proEditsMap[uid] || 0
        };
      });
    } catch (err) {
      console.error('User activity stats error', err);
      return userArr;
    }
  };

  // Reports data fetching functions
  const fetchReportsData = async () => {
    setReportsData(prev => ({ ...prev, loading: true }));
    
    try {
      const [purchases, timelineResult, deviceStats, teamStats, templateStats] = await Promise.all([
        fetchPurchasesData(),
        fetchTimelineData(),
        fetchDeviceStats(),
        fetchTeamStats(),
        fetchTemplateStats()
      ]);
      
      setReportsData({
        purchases,
        timelineData: timelineResult.data,
        totalLogins: timelineResult.totalLogins,
        deviceStats,
        teamStats,
        templateStats,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching reports data:', error);
      setReportsData(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchPurchasesData = async () => {
    try {
      // Get user IDs for this event
      const userIds = users.map(u => u.id || u.user_id || u.app_user_id);
      if (userIds.length === 0) return null;
      
      console.log('🔍 SALES DEBUG - Event ID:', eventId);
      console.log('🔍 SALES DEBUG - User IDs for this event:', userIds.slice(0, 5), '... (showing first 5 of', userIds.length, ')');
      
      // Check ALL orders for these users (regardless of status) to see what exists
      const { data: allUserOrders, error: allOrdersError } = await supabase
        .from('orders')
        .select('id, order_total, status, payment_status, created_at, user_id, event_id, payment_intent_id')
        .in('user_id', userIds);
      
      if (allOrdersError) {
        console.error('🔍 SALES DEBUG - Error fetching orders:', allOrdersError);
      } else {
        console.log('🔍 SALES DEBUG - Total orders found for these users:', allUserOrders?.length || 0);
        
        if (allUserOrders && allUserOrders.length > 0) {
          // Analyze what we found
          const statusCounts = {};
          const paymentStatusCounts = {};
          const eventIdCounts = {};
          
          allUserOrders.forEach(order => {
            statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
            paymentStatusCounts[order.payment_status] = (paymentStatusCounts[order.payment_status] || 0) + 1;
            const eventKey = order.event_id || 'null';
            eventIdCounts[eventKey] = (eventIdCounts[eventKey] || 0) + 1;
          });
          
          console.log('🔍 SALES DEBUG - Order status breakdown:', statusCounts);
          console.log('🔍 SALES DEBUG - Payment status breakdown:', paymentStatusCounts);
          console.log('🔍 SALES DEBUG - Event ID breakdown:', eventIdCounts);
          console.log('🔍 SALES DEBUG - Sample order:', allUserOrders[0]);
          
          // Check specifically for this event
          const thisEventOrders = allUserOrders.filter(o => o.event_id === eventId);
          console.log('🔍 SALES DEBUG - Orders for this specific event:', thisEventOrders.length);
          
          // Check for orders without event_id
          const noEventIdOrders = allUserOrders.filter(o => !o.event_id);
          console.log('🔍 SALES DEBUG - Orders without event_id:', noEventIdOrders.length);
        }
      }
      
      // Also check the order_history_view for this event
      const { data: orderHistoryData, error: historyError } = await supabase
        .from('order_history_view')
        .select('order_id, user_id, event_id, status, payment_status, order_total, fulfillment_status')
        .eq('event_id', eventId);
      
      if (historyError) {
        console.error('🔍 SALES DEBUG - Error fetching order history:', historyError);
      } else {
        console.log('🔍 SALES DEBUG - Order history view results for this event:', orderHistoryData?.length || 0);
        if (orderHistoryData && orderHistoryData.length > 0) {
          console.log('🔍 SALES DEBUG - Sample order history:', orderHistoryData[0]);
        }
      }
      
      // Get event registrations for comparison
      const { data: eventOrders, error: eventError } = await supabase
        .from('event_orders')
        .select('status, created_at, answers')
        .eq('event_id', eventId);
      
      if (eventError) {
        console.error('🔍 SALES DEBUG - Error fetching event orders:', eventError);
        throw eventError;
      }
      
      console.log('🔍 SALES DEBUG - Event orders found:', eventOrders?.length || 0);
      if (eventOrders && eventOrders.length > 0) {
        console.log('🔍 SALES DEBUG - Sample event order:', eventOrders[0]);
      }
      
      // Calculate metrics - use users.length as fallback if event_orders is empty
      const totalRegistrations = eventOrders.length > 0 ? eventOrders.length : users.length;
      const pendingRegistrations = eventOrders.filter(o => o.status === 'pending').length;
      const completedRegistrations = eventOrders.filter(o => o.status === 'completed').length;
      
      console.log('🔍 SALES DEBUG - Registration metrics:', {
        eventOrdersLength: eventOrders.length,
        usersLength: users.length,
        totalRegistrations,
        pendingRegistrations,
        completedRegistrations
      });
      
      // For now, use the order_history_view data if available, otherwise fall back to orders table
      let actualPurchases = 0;
      let totalSales = 0;
      
      if (!historyError && orderHistoryData && orderHistoryData.length > 0) {
        // Use order history view (includes fulfillment status)
        const completedOrders = orderHistoryData.filter(o => 
          o.payment_status === 'paid' || o.status === 'completed' || o.fulfillment_status === 'completed'
        );
        
        actualPurchases = completedOrders.length;
        totalSales = completedOrders.reduce((sum, order) => sum + (parseFloat(order.order_total) || 0), 0);
        
        console.log('📊 SALES REPORT - Using order_history_view:', {
          totalOrders: orderHistoryData.length,
          completedOrders: completedOrders.length,
          totalSales
        });
      } else if (allUserOrders && allUserOrders.length > 0) {
        // Fall back to direct orders table query
        const thisEventOrders = allUserOrders.filter(o => o.event_id === eventId);
        const completedOrders = thisEventOrders.filter(o => 
          o.payment_status === 'paid' || o.status === 'completed'
        );
        
        actualPurchases = completedOrders.length;
        totalSales = completedOrders.reduce((sum, order) => sum + (parseFloat(order.order_total) || 0), 0);
        
        console.log('📊 SALES REPORT - Using orders table:', {
          thisEventOrders: thisEventOrders.length,
          completedOrders: completedOrders.length,
          totalSales
        });
      }
      
      // Registration completion rate
      const completionRate = totalRegistrations > 0 ? (completedRegistrations / totalRegistrations * 100).toFixed(1) : 0;
      
      // Purchase conversion rate (purchases vs registrations)
      const conversionRate = totalRegistrations > 0 ? (actualPurchases / totalRegistrations * 100).toFixed(1) : 0;
      
      // Simple daily activity (we can enhance this later)
      const dailyActivity = {};
      
      // Add registration data
      eventOrders.forEach(order => {
        const date = new Date(order.created_at).toDateString();
        if (!dailyActivity[date]) dailyActivity[date] = { registrations: 0, completedRegistrations: 0, purchases: 0, sales: 0 };
        dailyActivity[date].registrations++;
        if (order.status === 'completed') dailyActivity[date].completedRegistrations++;
      });
      
      console.log('📊 SALES REPORT - Final results:', {
        totalRegistrations,
        actualPurchases,
        totalSales,
        conversionRate
      });
      
      return {
        totalRegistrations,
        completedRegistrations,
        pendingRegistrations,
        actualPurchases,
        totalSales,
        completionRate,
        conversionRate,
        dailyActivity
      };
    } catch (error) {
      console.error('Error fetching purchases data:', error);
      return null;
    }
  };

  const fetchTimelineData = async () => {
    try {
      const userIds = users.map(u => u.id || u.user_id || u.app_user_id);
      if (userIds.length === 0) return { data: [], totalLogins: 0 };
      
      console.log('🔍 LOGIN DEBUG - Fetching login data for', userIds.length, 'users');
      
      // Get ALL logins for these users (remove any potential limits)
      const { data: logins, error } = await supabase
        .from('user_logins')
        .select('created_at, user_id')
        .in('user_id', userIds)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching login timeline:', error);
        throw error;
      }
      
      console.log('🔍 LOGIN DEBUG - Found', logins?.length || 0, 'total login records');
      
      // Group by date and user
      const dailyLogins = {};
      const userLoginCounts = {};
      let totalLogins = 0;
      
      logins?.forEach(login => {
        totalLogins++;
        const date = new Date(login.created_at).toDateString();
        dailyLogins[date] = (dailyLogins[date] || 0) + 1;
        
        // Track per-user login counts
        userLoginCounts[login.user_id] = (userLoginCounts[login.user_id] || 0) + 1;
      });
      
      console.log('🔍 LOGIN DEBUG - Total logins calculated:', totalLogins);
      console.log('🔍 LOGIN DEBUG - Unique users with logins:', Object.keys(userLoginCounts).length);
      console.log('🔍 LOGIN DEBUG - Average logins per user:', totalLogins / Object.keys(userLoginCounts).length);
      
      // Show top 5 most active users
      const topUsers = Object.entries(userLoginCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);
      console.log('🔍 LOGIN DEBUG - Top 5 most active users:', topUsers);
      
      const timelineData = Object.entries(dailyLogins).map(([date, count]) => ({
        date,
        count
      }));
      
      // Return object with both data and totalLogins
      return {
        data: timelineData,
        totalLogins: totalLogins
      };
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      return { data: [], totalLogins: 0 };
    }
  };

  const fetchDeviceStats = async () => {
    try {
      const userIds = users.map(u => u.id || u.user_id || u.app_user_id);
      if (userIds.length === 0) return null;
      
      const { data: logins, error } = await supabase
        .from('user_logins')
        .select('user_agent')
        .in('user_id', userIds)
        .not('user_agent', 'is', null);
      
      if (error) throw error;
      
      const browsers = {};
      const devices = {};
      
      logins.forEach(login => {
        const ua = login.user_agent.toLowerCase();
        
        // Browser detection
        if (ua.includes('chrome')) browsers.Chrome = (browsers.Chrome || 0) + 1;
        else if (ua.includes('firefox')) browsers.Firefox = (browsers.Firefox || 0) + 1;
        else if (ua.includes('safari')) browsers.Safari = (browsers.Safari || 0) + 1;
        else if (ua.includes('edge')) browsers.Edge = (browsers.Edge || 0) + 1;
        else browsers.Other = (browsers.Other || 0) + 1;
        
        // Device detection
        if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
          devices.Mobile = (devices.Mobile || 0) + 1;
        } else if (ua.includes('tablet') || ua.includes('ipad')) {
          devices.Tablet = (devices.Tablet || 0) + 1;
        } else {
          devices.Desktop = (devices.Desktop || 0) + 1;
        }
      });
      
      return { browsers, devices };
    } catch (error) {
      console.error('Error fetching device stats:', error);
      return null;
    }
  };

  const fetchTeamStats = async () => {
    try {
      const teamStats = {};
      
      users.forEach(user => {
        const team = user.answers?.team_name || 'No Team';
        if (!teamStats[team]) {
          teamStats[team] = {
            registrations: 0,
            completed: 0,
            totalLogins: 0,
            mediaPics: 0,
            proEdits: 0
          };
        }
        
        teamStats[team].registrations++;
        if (user.order_status === 'completed') teamStats[team].completed++;
        teamStats[team].totalLogins += (user.login_stats?.count || 0);
        teamStats[team].mediaPics += (user.media_pics_count || 0);
        teamStats[team].proEdits += (user.pro_edits_count || 0);
      });
      
      return Object.entries(teamStats).map(([team, stats]) => ({
        team,
        ...stats
      })).sort((a, b) => b.registrations - a.registrations);
    } catch (error) {
      console.error('Error fetching team stats:', error);
      return [];
    }
  };

  const fetchTemplateStats = async () => {
    try {
      console.log('🔍 TEMPLATE STATS - Fetching template purchase data for event:', eventId);
      
      // First, get all paid orders for this event
      const { data: paidOrders, error: ordersError } = await supabase
        .from('order_history_view')
        .select('order_id, event_id, payment_status')
        .eq('event_id', eventId)
        .eq('payment_status', 'paid');
      
      if (ordersError) {
        console.error('Error fetching paid orders:', ordersError);
        return [];
      }
      
      console.log('🔍 TEMPLATE STATS - Found', paidOrders?.length || 0, 'paid orders for this event');
      
      if (!paidOrders || paidOrders.length === 0) {
        console.log('🔍 TEMPLATE STATS - No paid orders found, returning empty array');
        return [];
      }
      
      // Get order IDs
      const orderIds = paidOrders.map(order => order.order_id);
      
      // Get order items with export data to find the actual template_id
      const { data: orderItemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          name,
          quantity,
          price,
          order_id,
          export_id,
          exports!inner(
            id,
            template_id,
            export_url,
            export_url_wm
          )
        `)
        .in('order_id', orderIds)
        .not('export_id', 'is', null);
      
      if (itemsError) {
        console.error('Error fetching order items with exports:', itemsError);
        return [];
      }
      
      console.log('🔍 TEMPLATE STATS - Order items with exports:', orderItemsData?.length || 0, 'items');
      
      if (!orderItemsData || orderItemsData.length === 0) {
        console.log('🔍 TEMPLATE STATS - No order items with exports found');
        return [];
      }
      
      // Now get template details for the template IDs we found
      const templateIds = [...new Set(orderItemsData.map(item => item.exports.template_id).filter(Boolean))];
      console.log('🔍 TEMPLATE STATS - Unique template IDs found:', templateIds);
      
      let templatesData = [];
      if (templateIds.length > 0) {
        const { data: templates, error: templatesError } = await supabase
          .from('templates')
          .select('id, name, thumbnail_url, preview_url')
          .in('id', templateIds);
        
        if (templatesError) {
          console.warn('Error fetching template details:', templatesError);
        } else {
          templatesData = templates || [];
        }
      }
      
      console.log('🔍 TEMPLATE STATS - Template details fetched:', templatesData.length);
      
      // Create template lookup map
      const templateLookup = {};
      templatesData.forEach(template => {
        templateLookup[template.id] = template;
      });
      
      // Group by template and calculate stats
      const templateStats = {};
      
      orderItemsData.forEach(item => {
        const templateId = item.exports.template_id;
        const template = templateLookup[templateId];
        const templateName = template?.name || item.name || 'Unknown Template';
        const quantity = item.quantity || 1;
        const price = parseFloat(item.price || 0);
        
        const key = templateId || templateName; // Use template ID as key, fallback to name
        
        if (!templateStats[key]) {
          templateStats[key] = {
            templateId,
            templateName,
            thumbnailUrl: template?.thumbnail_url || template?.preview_url || item.exports.export_url_wm || item.exports.export_url,
            totalSold: 0,
            totalRevenue: 0,
            orderCount: 0,
            avgPrice: 0
          };
        }
        
        templateStats[key].totalSold += quantity;
        templateStats[key].totalRevenue += price * quantity;
        templateStats[key].orderCount += 1;
      });
      
      // Calculate average price and sort by total sold
      const result = Object.values(templateStats).map(template => ({
        ...template,
        avgPrice: template.totalSold > 0 ? template.totalRevenue / template.totalSold : 0
      })).sort((a, b) => b.totalSold - a.totalSold);
      
      console.log('🔍 TEMPLATE STATS - Processed template stats:', result.length, 'unique templates');
      console.log('🔍 TEMPLATE STATS - Top 3 templates:', result.slice(0, 3));
      
      return result;
    } catch (error) {
      console.error('Error fetching template stats:', error);
      return [];
    }
  };
  
  const [detailUser, setDetailUser] = useState(null);
  const { isOpen: isDetailOpen, onOpen: openDetail, onClose: closeDetail } = useDisclosure();
  
  // Reports state
  const [reportsData, setReportsData] = useState({
    purchases: null,
    timelineData: [],
    totalLogins: 0,
    deviceStats: null,
    teamStats: [],
    templateStats: [],
    loading: true
  });
  
  // Modal states for card details
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [salesBreakdownData, setSalesBreakdownData] = useState([]);
  const [salesBreakdownLoading, setSalesBreakdownLoading] = useState(false);
  
  // NOTE: No longer deduplicating by user ID. If a parent has multiple registrations (e.g., multiple children),
  // we want to show each registration separately in the Users table.
  // Keeping the helper here (unused) in case other parts of the component rely on it in the future.
  const uniqueById = (arr) => [...new Map(arr.map(u=>[u.id,u])).values()];
  
  const handleImpersonateUser = async (user) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Authentication Error',
          description: 'You must be logged in to impersonate users.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Store the current session for later restoration
      localStorage.setItem('staff_session', JSON.stringify(session));

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: {
          staff_user_id: session.user.id,
          target_user_id: user.id,
          event_id: eventId
        }
      });

      if (error) {
        console.error('Impersonation error:', error);
        toast({
          title: 'Impersonation Failed',
          description: error.message || 'Could not impersonate user. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!data?.session?.access_token) {
        console.error('Invalid session data received:', data);
        toast({
          title: 'Impersonation Failed',
          description: 'Invalid session data received from server.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Set the new session
      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token
      });

      if (setSessionError) {
        console.error('Session error:', setSessionError);
        toast({
          title: 'Login Failed',
          description: 'Could not log in as user. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Redirect to the user's dashboard
      navigate('/dashboard');

    } catch (error) {
      console.error('Impersonation error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const fetchSalesBreakdown = async () => {
    try {
      setSalesBreakdownLoading(true);
      
      // Get user IDs for this event
      const userIds = users.map(u => u.id || u.user_id || u.app_user_id);
      if (userIds.length === 0) return [];
      
      console.log('🔍 SALES BREAKDOWN - Fetching detailed sales for', userIds.length, 'users');
      
      // Get orders with user details
      const { data: ordersWithUsers, error: ordersError } = await supabase
        .from('order_history_view')
        .select('*')
        .eq('event_id', eventId)
        .in('payment_status', ['paid'])
        .order('order_date', { ascending: false });
      
      if (ordersError) {
        console.error('Error fetching sales breakdown:', ordersError);
        throw ordersError;
      }
      
      console.log('🔍 SALES BREAKDOWN - Found', ordersWithUsers?.length || 0, 'paid orders');
      
      // Get user details for these orders
      const orderUserIds = ordersWithUsers?.map(order => order.user_id) || [];
      const { data: userDetailsApp, error: userErrorApp } = await supabase
        .from('app_users')
        .select('id, first_name, last_name, email')
        .in('id', orderUserIds);
      
      if (userErrorApp) {
        console.warn('fetchSalesBreakdown: app_users query error:', userErrorApp);
      }
      
      // Determine which IDs are still missing
      const foundIds = (userDetailsApp || []).map(u => u.id);
      const missingIds = orderUserIds.filter(id => !foundIds.includes(id));
      let userDetailsAuth = [];
      if (missingIds.length > 0) {
        const { data: authUsers, error: authErr } = await supabase
          .from('auth.users')
          .select('id, email')
          .in('id', missingIds);
        if (authErr) {
          console.warn('fetchSalesBreakdown: auth.users query error:', authErr);
        } else {
          userDetailsAuth = authUsers.map(u => ({ id: u.id, first_name: 'Unknown', last_name: 'User', email: u.email }));
        }
      }
      const userDetails = [...(userDetailsApp || []), ...userDetailsAuth];
      
      // Get order items for detailed breakdown
      const orderIds = ordersWithUsers?.map(order => order.order_id) || [];
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select('order_id, name, price, quantity, item_type')
        .in('order_id', orderIds);
      
      if (itemsError) {
        console.error('Error fetching order items:', itemsError);
      }
      
      // Create user lookup map
      const userMap = {};
      userDetails?.forEach(user => {
        userMap[user.id] = user;
      });
      
      // Create order items lookup map
      const itemsMap = {};
      orderItems?.forEach(item => {
        if (!itemsMap[item.order_id]) itemsMap[item.order_id] = [];
        itemsMap[item.order_id].push(item);
      });
      
      // Combine data
      const breakdown = ordersWithUsers?.map(order => {
        const user = userMap[order.user_id];
        const items = itemsMap[order.order_id] || [];
        
        return {
          orderId: order.order_id,
          orderDate: order.order_date,
          userId: order.user_id,
          userName: user ? `${user.first_name} ${user.last_name}` : 'Unknown User',
          userEmail: user?.email || 'Unknown Email',
          packageName: order.package_name,
          orderTotal: parseFloat(order.order_total || 0),
          itemCount: order.total_items_count || 0,
          items: items,
          eventName: order.event_name
        };
      }) || [];
      
      console.log('🔍 SALES BREAKDOWN - Processed breakdown for', breakdown.length, 'orders');
      setSalesBreakdownData(breakdown);
      
    } catch (error) {
      console.error('Error fetching sales breakdown:', error);
      setSalesBreakdownData([]);
    } finally {
      setSalesBreakdownLoading(false);
    }
  };
  
  const handleSalesCardClick = async () => {
    setSalesModalOpen(true);
    await fetchSalesBreakdown();
  };
  
  if (loading) {
    return (
      <Flex justify="center" align="center" height="80vh" width="100%">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text ml={4} fontSize="xl">Loading event dashboard...</Text>
      </Flex>
    );
  }
  
  return (
    <>
      {/* Mobile Header with back button */}
      <MobileHeader 
        user={user} 
        backLink="/dashboard/events" 
      />
      
      {/* Mobile menu overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={toggleMobileMenu}
      ></div>
      
      <div ref={containerRef} style={{ touchAction: 'auto', height: '100%' }}>
        <Box className="event-dashboard-container" p={4}>
          {/* Desktop back button */}
          <Link to="/dashboard/events" className="desktop-only">
            <Button 
              leftIcon={<FaAngleLeft />} 
              variant="ghost" 
              mb={4}
              size="sm"
            >
              Back to Events
            </Button>
          </Link>
          
          {event && (
            <>
              <Card 
                mb={6} 
                bg={headerBgColor} 
                color="white"
                borderRadius="lg"
                boxShadow="md"
                overflow="hidden"
              >
                <CardBody>
                  <Flex justify="space-between" align="center" wrap="wrap">
                    <Box>
                      <Heading size="lg">{event.name}</Heading>
                      <HStack mt={2}>
                        <FaCalendarAlt />
                        <Text>{formatDate(event.start_date)}{event.end_date && event.start_date !== event.end_date ? ` - ${formatDate(event.end_date)}` : ''}</Text>
                      </HStack>
                      <HStack mt={2}>
                        <FaMapMarkerAlt />
                        <Text>{event.city}, {event.state}</Text>
                      </HStack>
                    </Box>
                    <HStack spacing={4}>
                      <Box textAlign="center">
                        <Text fontSize="sm">Status</Text>
                        <Badge 
                          colorScheme={
                            event.status === 'active' ? 'green' : 
                            event.status === 'upcoming' ? 'yellow' :
                            event.status === 'completed' ? 'gray' : 'red'
                          }
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="sm"
                        >
                          {(event.status || 'PENDING').toUpperCase()}
                        </Badge>
                      </Box>
                      <Box textAlign="center">
                        <Text fontSize="sm">Participants</Text>
                        <HStack align="center" justify="center">
                          <FaUserFriends />
                          <Text fontWeight="bold">{users.length}</Text>
                        </HStack>
                      </Box>
                      <Button
                        leftIcon={<FaEdit />}
                        colorScheme="whiteAlpha"
                        size="sm"
                        onClick={() => navigate(`/dashboard/events/${eventId}/edit`)}
                      >
                        Edit Event
                      </Button>
                      {isAdmin && (
                        <Button
                          leftIcon={<FaUserPlus />}
                          colorScheme="whiteAlpha"
                          size="sm"
                          onClick={() => navigate(`/dashboard/events/${eventId}/bulk-signup`)}
                        >
                          Bulk Signup
                        </Button>
                      )}
                      {isAdmin && (
                        <IconButton
                          icon={<FaBug />}
                          aria-label="Debug Event"
                          colorScheme="whiteAlpha"
                          size="sm"
                          onClick={runDiagnostics}
                          title="Show Debug Information"
                        />
                      )}
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>
              
              {/* Debug modal */}
              <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Event Debug Information</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    {debugData ? (
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Source</Th>
                            <Th isNumeric>Count</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {debugData.map((item, index) => (
                            <Tr key={index}>
                              <Td>{item.source}</Td>
                              <Td isNumeric>{item.count}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Spinner />
                    )}
                    
                    <Divider my={4} />
                    
                    <Box mt={4}>
                      <Heading size="sm" mb={2}>Event Details</Heading>
                      <Code p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap">
                        {JSON.stringify(event, null, 2)}
                      </Code>
                    </Box>
                    
                    <Box mt={4}>
                      <Heading size="sm" mb={2}>Loaded Users Count: {users.length}</Heading>
                      <Text fontSize="sm" mb={2}>Event ID: {eventId}</Text>
                    </Box>
                  </ModalBody>
                  <ModalFooter>
                    <Button colorScheme="blue" onClick={onClose}>
                      Close
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              
              {/* User Details modal */}
              <Modal isOpen={isDetailOpen} onClose={closeDetail} size="lg">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>User Details</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    {detailUser ? (
                      <VStack align="stretch" spacing={3} fontSize="sm">
                        <Box>
                          <Text fontWeight="bold">Parent / Guardian</Text>
                          <Text>{detailUser.first_name} {detailUser.last_name}</Text>
                          <Text>{detailUser.email}</Text>
                          {detailUser.phone && <Text>{detailUser.phone}</Text>}
                        </Box>
                        <Divider />
                        <Box>
                          <Text fontWeight="bold">Athlete</Text>
                          <Text>{detailUser.answers?.athlete_first_name || ''} {detailUser.answers?.athlete_last_name || ''}</Text>
                          {detailUser.answers?.team_name && <Text>Team: {detailUser.answers.team_name}</Text>}
                        </Box>
                        <Divider />
                        <Box>
                          <Text fontWeight="bold">Order Status</Text>
                          <Badge colorScheme={detailUser.order_status === 'completed' ? 'green' : detailUser.order_status === 'pending' ? 'yellow' : 'gray'}>
                            {detailUser.order_status?.toUpperCase()}
                          </Badge>
                          {detailUser.order_created_at && (
                            <Text mt={1}>Order date: {formatDate(detailUser.order_created_at)}</Text>
                          )}
                        </Box>
                        <Divider />
                        <Box>
                          <Text fontWeight="bold">Activity Summary</Text>
                          <Text>Total Logins: {detailUser.login_stats?.count || 0}</Text>
                          <Text>Last Login: {detailUser.login_stats?.last ? formatDate(detailUser.login_stats.last) : '—'}</Text>
                          <Text>Media Day Pics: {detailUser.media_pics_count || 0}</Text>
                          <Text>Pro Edits: {detailUser.pro_edits_count || 0}</Text>
                        </Box>
                        <Divider />
                        <Box>
                          <Text fontWeight="bold">Raw Record</Text>
                          <Code p={2} borderRadius="md" whiteSpace="pre-wrap" width="100%">
                            {JSON.stringify(detailUser, null, 2)}
                          </Code>
                        </Box>
                      </VStack>
                    ) : <Spinner />}
                  </ModalBody>
                  <ModalFooter>
                    <Button onClick={closeDetail}>Close</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* Magic Link modal */}
              <Modal isOpen={isLinkOpen} onClose={closeLinkModal} size="lg">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Manual Magic Link</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text mb={2}>Copy and share this link with the customer:</Text>
                    <Code p={2} borderRadius="md" whiteSpace="pre-wrap" display="block" mb={4}>{linkToCopy}</Code>
                    <Button leftIcon={<FaCopy />} colorScheme="blue" onClick={() => { navigator.clipboard.writeText(linkToCopy); toast({ title: 'Copied', status: 'success', duration: 2000 }); }}>
                      Copy to Clipboard
                    </Button>
                  </ModalBody>
                  <ModalFooter>
                    <Button onClick={closeLinkModal}>Close</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              
              <Tabs variant="enclosed" colorScheme="blue" borderRadius="md" boxShadow="sm" bg={bgColor}>
                <TabList>
                  <Tab>Users</Tab>
                  <Tab>Event Details</Tab>
                  <Tab>Reports</Tab>
                </TabList>
                
                <TabPanels>
                  <TabPanel>
                    <Box mb={4}>
                      <HStack mb={4} spacing={4} align="stretch" flexWrap="wrap">
                        <InputGroup maxW="300px">
                          <InputLeftElement pointerEvents="none">
                            <FaSearch color="gray.300" />
                          </InputLeftElement>
                          <Input 
                            placeholder="Search by name, email, team…" 
                            value={searchQuery}
                            onChange={handleSearch}
                            borderRadius="md"
                          />
                        </InputGroup>
                        <Box minW="200px">
                          <select
                            value={teamFilter}
                            onChange={handleTeamFilter}
                            style={{ padding: '8px', borderRadius: '6px', borderColor: borderColor, width: '100%' }}
                          >
                            <option value="">All Teams</option>
                            {Array.from(new Set(users.map(u => u.answers?.team_name).filter(Boolean)))
                              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                              .map(team => (
                                <option key={team} value={team}>{team}</option>
                              ))}
                          </select>
                        </Box>
                      </HStack>
                    </Box>
                    
                    {filteredUsers.length === 0 ? (
                      <Box textAlign="center" my={8} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <FaUserFriends size={40} style={{ margin: '0 auto', color: 'gray' }} />
                        <Text mt={4} fontSize="lg" fontWeight="medium">No users found</Text>
                        <Text color="gray.500">
                          {searchQuery ? 'Try a different search term' : 'There are no users registered for this event yet'}
                        </Text>
                      </Box>
                    ) : (
                      <Box overflowX="auto">
                        <Table variant="simple" borderRadius="md">
                          <Thead>
                            <Tr>
                              <Th>Sent</Th>
                              <Th>Athlete</Th>
                              <Th>User</Th>
                              <Th>Team</Th>
                              <Th>Activity</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredUsers.map((user, idx) => {
                              const rowKey = `${user.id}-${idx}`;
                              return (
                              <Tr key={rowKey}
                                  cursor="pointer"
                                  _hover={{ bg: 'gray.50' }}
                                  onClick={() => handleUploadImages(user.id)}
                              >
                                <Td>
                                  <Checkbox isChecked={loginSentMap[rowKey]} colorScheme="green" pointerEvents="none" />
                                </Td>
                                <Td>
                                  <Text fontSize="sm" fontWeight="bold">
                                    {user.answers?.athlete_first_name || ''} {user.answers?.athlete_last_name || ''}
                                  </Text>
                                </Td>
                                <Td>
                                  <Flex align="center">
                                    <Avatar 
                                      size="sm" 
                                      name={`${user.first_name} ${user.last_name}`} 
                                      src={user.profile_image} 
                                      mr={2} 
                                    />
                                    <Box>
                                      <Text>{user.first_name} {user.last_name}</Text>
                                    </Box>
                                  </Flex>
                                </Td>
                                <Td>
                                  <Text fontSize="sm">{user.answers?.team_name || '—'}</Text>
                                </Td>
                                <Td>
                                  <VStack align="start" spacing={1}>
                                    <Tooltip
                                      label={`Last login: ${user.login_stats?.last ? formatDate(user.login_stats.last) : '—'}`}
                                      hasArrow
                                    >
                                      <Badge colorScheme="blue" size="sm">
                                        {user.login_stats?.count || 0} logins
                                      </Badge>
                                    </Tooltip>
                                    <Badge colorScheme="green" size="sm">
                                      {user.media_pics_count || 0} media pics
                                    </Badge>
                                    <Badge colorScheme="purple" size="sm">
                                      {user.pro_edits_count || 0} pro edits
                                    </Badge>
                                  </VStack>
                                </Td>
                                <Td>
                                  <HStack spacing={2}>
                                    <IconButton
                                      aria-label="Upload images"
                                      icon={<FaCamera />}
                                      size="sm"
                                      colorScheme="blue"
                                      onClick={(e) => { e.stopPropagation(); handleUploadImages(user.id); }}
                                      title="Upload images for this user"
                                    />
                                    <IconButton
                                      aria-label="Send login email"
                                      icon={<FaPaperPlane />}
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => { e.stopPropagation(); sendLoginEmail(user, rowKey); }}
                                      title="Send login email"
                                    />
                                    {user.phone && (
                                      <IconButton
                                        aria-label="Send login text"
                                        icon={<FaPhone />}
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); sendLoginText(user, rowKey); }}
                                        title="Send login text"
                                      />
                                    )}
                                    <IconButton
                                      aria-label="Generate magic link"
                                      icon={<FaLink />}
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => { e.stopPropagation(); generateMagicLink(user); }}
                                      title="Generate magic link"
                                    />
                                    {isAdmin && (
                                      <IconButton
                                        aria-label="Login as user"
                                        icon={<FaUserSecret />}
                                        size="sm"
                                        colorScheme="purple"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); handleImpersonateUser(user); }}
                                        title="Login as this user"
                                      />
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="teal"
                                      onClick={(e) => { e.stopPropagation(); setDetailUser(user); openDetail(); }}
                                    >
                                      View Details
                                    </Button>
                                  </HStack>
                                </Td>
                              </Tr>
                            );})}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </TabPanel>
                  
                  <TabPanel>
                    <VStack align="stretch" spacing={4}>
                      <Heading size="md">Event Information</Heading>
                      
                      <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                        <Text fontWeight="bold">Description</Text>
                        <Text mt={2}>{event.description || 'No description provided'}</Text>
                      </Box>
                      
                      <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                        <Text fontWeight="bold">Location</Text>
                        <Text mt={2}>
                          {event.address1}
                          {event.address2 && <>, {event.address2}</>}<br />
                          {event.city}, {event.state} {event.zip}
                        </Text>
                      </Box>
                      
                      <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                        <Text fontWeight="bold">Event Type</Text>
                        <Badge mt={2} colorScheme="purple">{event.event_type || 'General'}</Badge>
                      </Box>
                    </VStack>
                  </TabPanel>
                  
                  <TabPanel>
                    {reportsData.loading ? (
                      <Flex justify="center" align="center" height="400px">
                        <Spinner size="xl" color="blue.500" />
                        <Text ml={4}>Loading analytics...</Text>
                      </Flex>
                    ) : (
                      <VStack spacing={8} align="stretch">
                        {/* Overview Cards */}
                        <Flex justifyContent="space-between" flexWrap="wrap" gap={4}>
                          <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
                            <CardBody>
                              <Flex direction="column" align="center" textAlign="center">
                                <Heading 
                                  size={(() => {
                                    const text = users.length.toString();
                                    if (text.length <= 3) return "4xl";      // 999
                                    if (text.length <= 4) return "3xl";      // 9,999
                                    return "2xl";                             // 99,999+
                                  })()} 
                                  color="blue.500" 
                                  mb={2}
                                  noOfLines={1}
                                >
                                  {users.length}
                                </Heading>
                                <Text fontWeight="medium">Total Registrations</Text>
                              </Flex>
                            </CardBody>
                          </Card>
                          <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
                            <CardBody>
                              <Flex direction="column" align="center" textAlign="center">
                                <Heading 
                                  size={(() => {
                                    const text = (reportsData.purchases?.actualPurchases || 0).toString();
                                    if (text.length <= 3) return "4xl";      // 999
                                    if (text.length <= 4) return "3xl";      // 9,999
                                    return "2xl";                             // 99,999+
                                  })()} 
                                  color="green.500" 
                                  mb={2}
                                  noOfLines={1}
                                >
                                  {reportsData.purchases?.actualPurchases || 0}
                                </Heading>
                                <Text fontWeight="medium">Completed Orders</Text>
                                <Text fontSize="sm" color="gray.500">
                                  {reportsData.purchases?.conversionRate || 0}% conversion rate
                                </Text>
                              </Flex>
                            </CardBody>
                          </Card>
                          <Card 
                            flex={1} 
                            minW="200px" 
                            boxShadow="sm" 
                            borderRadius="lg"
                            cursor="pointer"
                            transition="all 0.2s"
                            _hover={{ 
                              boxShadow: "md", 
                              transform: "translateY(-2px)",
                              borderColor: "yellow.300"
                            }}
                            onClick={handleSalesCardClick}
                          >
                            <CardBody>
                              <Flex direction="column" align="center" textAlign="center">
                                <Heading 
                                  size={(() => {
                                    const salesAmount = reportsData.purchases?.totalSales?.toFixed(2) || '0.00';
                                    const salesText = `$${salesAmount}`;
                                    if (salesText.length <= 6) return "4xl";      // $99.99
                                    if (salesText.length <= 8) return "3xl";      // $999.99
                                    if (salesText.length <= 10) return "2xl";     // $9,999.99
                                    if (salesText.length <= 12) return "xl";      // $99,999.99
                                    return "lg";                                   // $999,999.99+
                                  })()} 
                                  color="yellow.500" 
                                  mb={2}
                                  noOfLines={1}
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                >
                                  ${reportsData.purchases?.totalSales?.toFixed(2) || '0.00'}
                                </Heading>
                                <Text fontWeight="medium">Total Sales</Text>
                                <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                  Avg: ${reportsData.purchases?.actualPurchases > 0 ? (reportsData.purchases.totalSales / reportsData.purchases.actualPurchases).toFixed(2) : '0.00'}
                                </Text>
                                <Text fontSize="xs" color="blue.500" mt={1}>
                                  Click for details
                                </Text>
                              </Flex>
                            </CardBody>
                          </Card>
                          <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
                            <CardBody>
                              <Flex direction="column" align="center" textAlign="center">
                                <Heading 
                                  size={(() => {
                                    const totalLogins = reportsData.totalLogins || reportsData.timelineData?.reduce((sum, day) => sum + day.count, 0) || 0;
                                    const text = totalLogins.toString();
                                    if (text.length <= 3) return "4xl";      // 999
                                    if (text.length <= 4) return "3xl";      // 9,999
                                    return "2xl";                             // 99,999+
                                  })()} 
                                  color="orange.500" 
                                  mb={2}
                                  noOfLines={1}
                                >
                                  {(() => {
                                    console.log('🔍 TOTAL LOGINS DEBUG - timelineData:', reportsData.timelineData);
                                    console.log('🔍 TOTAL LOGINS DEBUG - totalLogins property:', reportsData.totalLogins);
                                    const calculated = reportsData.timelineData?.reduce((sum, day) => sum + day.count, 0) || 0;
                                    console.log('🔍 TOTAL LOGINS DEBUG - calculated from reduce:', calculated);
                                    return reportsData.totalLogins || calculated;
                                  })()}
                                </Heading>
                                <Text fontWeight="medium">Total Logins</Text>
                                <Text fontSize="sm" color="gray.500">
                                  Across all users
                                </Text>
                              </Flex>
                            </CardBody>
                          </Card>
                          <Card flex={1} minW="200px" boxShadow="sm" borderRadius="lg">
                            <CardBody>
                              <Flex direction="column" align="center" textAlign="center">
                                <Heading 
                                  size={(() => {
                                    const avgValue = reportsData.purchases?.actualPurchases > 0 ? 
                                      `$${(reportsData.purchases.totalSales / reportsData.purchases.actualPurchases).toFixed(0)}` : 
                                      '$0';
                                    if (avgValue.length <= 4) return "4xl";      // $999
                                    if (avgValue.length <= 6) return "3xl";      // $9,999
                                    return "2xl";                                 // $99,999+
                                  })()} 
                                  color="purple.500" 
                                  mb={2}
                                  noOfLines={1}
                                >
                                  {reportsData.purchases?.actualPurchases > 0 ? 
                                    `$${(reportsData.purchases.totalSales / reportsData.purchases.actualPurchases).toFixed(0)}` : 
                                    '$0'
                                  }
                                </Heading>
                                <Text fontWeight="medium">Avg Order Value</Text>
                                <Text fontSize="sm" color="gray.500">
                                  Per completed order
                                </Text>
                              </Flex>
                            </CardBody>
                          </Card>
                        </Flex>

                        {/* Purchase Analytics */}
                        {reportsData.purchases && (
                          <Card>
                            <CardBody>
                              <Heading size="md" mb={4}>📊 Sales & Revenue Analytics</Heading>
                              <Flex flexWrap="wrap" gap={6}>
                                <Box flex={1} minW="300px">
                                  <Text fontWeight="bold" mb={2}>Registration vs Purchase Breakdown</Text>
                                  <VStack align="stretch" spacing={2}>
                                    <Flex justify="space-between" align="center">
                                      <HStack>
                                        <Badge colorScheme="blue">Total Registrations</Badge>
                                        <Text>{reportsData.purchases.totalRegistrations}</Text>
                                      </HStack>
                                      <Text fontWeight="bold">100%</Text>
                                    </Flex>
                                    <Flex justify="space-between" align="center">
                                      <HStack>
                                        <Badge colorScheme="green">Completed Orders</Badge>
                                        <Text>{reportsData.purchases.actualPurchases}</Text>
                                      </HStack>
                                      <Text fontWeight="bold">{reportsData.purchases.conversionRate}%</Text>
                                    </Flex>
                                    <Flex justify="space-between" align="center">
                                      <HStack>
                                        <Badge colorScheme="yellow">Pending Registrations</Badge>
                                        <Text>{reportsData.purchases.pendingRegistrations}</Text>
                                      </HStack>
                                      <Text>{((reportsData.purchases.pendingRegistrations / reportsData.purchases.totalRegistrations) * 100).toFixed(1)}%</Text>
                                    </Flex>
                                    <Divider />
                                    <Flex justify="space-between" align="center">
                                      <HStack>
                                        <Badge colorScheme="purple">Total Revenue</Badge>
                                        <Text fontWeight="bold">${reportsData.purchases.totalSales.toFixed(2)}</Text>
                                      </HStack>
                                      <Text fontSize="sm" color="gray.500">
                                        Avg: ${reportsData.purchases.actualPurchases > 0 ? (reportsData.purchases.totalSales / reportsData.purchases.actualPurchases).toFixed(2) : '0.00'}
                                      </Text>
                                    </Flex>
                                  </VStack>
                                </Box>
                                <Box flex={1} minW="300px">
                                  <Text fontWeight="bold" mb={2}>Revenue Insights</Text>
                                  <VStack align="stretch" spacing={2}>
                                    <Flex justify="space-between" align="center">
                                      <Text>Revenue per Registration:</Text>
                                      <Text fontWeight="bold">
                                        ${reportsData.purchases.totalRegistrations > 0 ? 
                                          (reportsData.purchases.totalSales / reportsData.purchases.totalRegistrations).toFixed(2) : 
                                          '0.00'
                                        }
                                      </Text>
                                    </Flex>
                                    <Flex justify="space-between" align="center">
                                      <Text>Conversion Rate:</Text>
                                      <Text fontWeight="bold" color="green.500">{reportsData.purchases.conversionRate}%</Text>
                                    </Flex>
                                    <Flex justify="space-between" align="center">
                                      <Text>Potential Revenue:</Text>
                                      <Text fontWeight="bold" color="blue.500">
                                        ${reportsData.purchases.actualPurchases > 0 ? 
                                          ((reportsData.purchases.totalSales / reportsData.purchases.actualPurchases) * reportsData.purchases.totalRegistrations).toFixed(2) :
                                          '0.00'
                                        }
                                      </Text>
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500">
                                      *If all registrations converted at current AOV
                                    </Text>
                                  </VStack>
                                </Box>
                                <Box flex={1} minW="300px">
                                  <Text fontWeight="bold" mb={2}>Daily Activity</Text>
                                  <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                                    {Object.entries(reportsData.purchases.dailyActivity).map(([date, stats]) => (
                                      <Flex key={date} justify="space-between" fontSize="sm" p={2} borderWidth="1px" borderRadius="md">
                                        <Text fontWeight="medium">{new Date(date).toLocaleDateString()}</Text>
                                        <HStack spacing={1}>
                                          <Badge size="sm" colorScheme="blue">{stats.registrations}R</Badge>
                                          <Badge size="sm" colorScheme="green">{stats.purchases}P</Badge>
                                          {stats.sales > 0 && (
                                            <Badge size="sm" colorScheme="purple">${stats.sales.toFixed(0)}</Badge>
                                          )}
                                        </HStack>
                                      </Flex>
                                    ))}
                                  </VStack>
                                  <Text fontSize="xs" color="gray.500" mt={2}>
                                    R = Registrations, P = Purchases
                                  </Text>
                                </Box>
                              </Flex>
                            </CardBody>
                          </Card>
                        )}

                        {/* Template Purchase Breakdown */}
                        {reportsData.templateStats && reportsData.templateStats.length > 0 && (
                          <Card>
                            <CardBody>
                              <Heading size="md" mb={4}>🎨 Template Purchase Breakdown</Heading>
                              <Text fontSize="sm" color="gray.600" mb={4}>
                                Analysis of which image templates were purchased and their performance
                              </Text>
                              
                              {/* Summary Stats */}
                              <Flex gap={4} mb={6} flexWrap="wrap">
                                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                                  <Text fontSize="sm" color="gray.600">Unique Templates Sold</Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                                    {reportsData.templateStats.length}
                                  </Text>
                                </Box>
                                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                                  <Text fontSize="sm" color="gray.600">Total Items Sold</Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                                    {reportsData.templateStats.reduce((sum, template) => sum + template.totalSold, 0)}
                                  </Text>
                                </Box>
                                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                                  <Text fontSize="sm" color="gray.600">Template Revenue</Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                                    ${reportsData.templateStats.reduce((sum, template) => sum + template.totalRevenue, 0).toFixed(2)}
                                  </Text>
                                </Box>
                                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                                  <Text fontSize="sm" color="gray.600">Avg Revenue/Template</Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                                    ${reportsData.templateStats.length > 0 ? 
                                      (reportsData.templateStats.reduce((sum, template) => sum + template.totalRevenue, 0) / reportsData.templateStats.length).toFixed(2) : 
                                      '0.00'
                                    }
                                  </Text>
                                </Box>
                              </Flex>

                              {/* Detailed Template Table */}
                              <Box overflowX="auto">
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Template</Th>
                                      <Th isNumeric>Units Sold</Th>
                                      <Th isNumeric>Orders</Th>
                                      <Th isNumeric>Revenue</Th>
                                      <Th isNumeric>Avg Price</Th>
                                      <Th isNumeric>% of Total Sales</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {reportsData.templateStats.map((template, index) => {
                                      const totalSold = reportsData.templateStats.reduce((sum, t) => sum + t.totalSold, 0);
                                      const salesPercentage = totalSold > 0 ? (template.totalSold / totalSold * 100).toFixed(1) : '0.0';
                                      
                                      return (
                                        <Tr key={index}>
                                          <Td>
                                            <Flex align="center" gap={3}>
                                              {template.thumbnailUrl && (
                                                <Box 
                                                  borderRadius="md" 
                                                  overflow="hidden" 
                                                  width="50px" 
                                                  height="50px"
                                                  flexShrink={0}
                                                  bg="gray.100"
                                                >
                                                  <img 
                                                    src={template.thumbnailUrl}
                                                    alt={template.templateName}
                                                    style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                      e.target.style.display = 'none';
                                                    }}
                                                  />
                                                </Box>
                                              )}
                                              <Box>
                                                <Text fontWeight="medium" noOfLines={1}>
                                                  {template.templateName}
                                                </Text>
                                                {template.templateId && (
                                                  <Text fontSize="xs" color="gray.500">
                                                    ID: {template.templateId}
                                                  </Text>
                                                )}
                                              </Box>
                                            </Flex>
                                          </Td>
                                          <Td isNumeric>
                                            <Badge colorScheme="blue" size="sm">
                                              {template.totalSold}
                                            </Badge>
                                          </Td>
                                          <Td isNumeric>{template.orderCount}</Td>
                                          <Td isNumeric fontWeight="medium">
                                            ${template.totalRevenue.toFixed(2)}
                                          </Td>
                                          <Td isNumeric>
                                            ${template.avgPrice.toFixed(2)}
                                          </Td>
                                          <Td isNumeric>
                                            <Text fontWeight="medium" color="green.500">
                                              {salesPercentage}%
                                            </Text>
                                          </Td>
                                        </Tr>
                                      );
                                    })}
                                  </Tbody>
                                </Table>
                              </Box>
                              
                              {reportsData.templateStats.length === 0 && (
                                <Box textAlign="center" py={8}>
                                  <Text color="gray.500">No template purchase data available</Text>
                                </Box>
                              )}
                            </CardBody>
                          </Card>
                        )}

                        {/* Device & Browser Stats */}
                        {reportsData.deviceStats && (
                          <Flex gap={4} flexWrap="wrap">
                            <Card flex={1} minW="300px">
                              <CardBody>
                                <Heading size="md" mb={4}>📱 Device Types</Heading>
                                <VStack align="stretch" spacing={2}>
                                  {Object.entries(reportsData.deviceStats.devices).map(([device, count]) => {
                                    const totalDevices = Object.values(reportsData.deviceStats.devices).reduce((sum, c) => sum + c, 0);
                                    const percentage = totalDevices > 0 ? ((count / totalDevices) * 100).toFixed(1) : '0.0';
                                    
                                    return (
                                      <Flex key={device} justify="space-between" align="center">
                                        <Text>{device}</Text>
                                        <HStack>
                                          <Badge colorScheme="purple">{count}</Badge>
                                          <Text fontSize="sm" color="gray.500">({percentage}%)</Text>
                                        </HStack>
                                      </Flex>
                                    );
                                  })}
                                </VStack>
                              </CardBody>
                            </Card>
                            <Card flex={1} minW="300px">
                              <CardBody>
                                <Heading size="md" mb={4}>🌐 Browser Usage</Heading>
                                <VStack align="stretch" spacing={2}>
                                  {Object.entries(reportsData.deviceStats.browsers).map(([browser, count]) => {
                                    const totalBrowsers = Object.values(reportsData.deviceStats.browsers).reduce((sum, c) => sum + c, 0);
                                    const percentage = totalBrowsers > 0 ? ((count / totalBrowsers) * 100).toFixed(1) : '0.0';
                                    
                                    return (
                                      <Flex key={browser} justify="space-between" align="center">
                                        <Text>{browser}</Text>
                                        <HStack>
                                          <Badge colorScheme="teal">{count}</Badge>
                                          <Text fontSize="sm" color="gray.500">({percentage}%)</Text>
                                        </HStack>
                                      </Flex>
                                    );
                                  })}
                                </VStack>
                              </CardBody>
                            </Card>
                          </Flex>
                        )}

                        {/* Activity Timeline */}
                        {reportsData.timelineData.length > 0 && (
                          <Card>
                            <CardBody>
                              <Heading size="md" mb={4}>📈 Login Activity Timeline</Heading>
                              <Text fontSize="sm" color="gray.600" mb={4}>
                                Daily login activity showing user engagement over time
                              </Text>
                              <Box maxH="300px" overflowY="auto">
                                {reportsData.timelineData.map((day, index) => (
                                  <Flex key={index} justify="space-between" align="center" py={2} borderBottomWidth="1px">
                                    <Text>{new Date(day.date).toLocaleDateString()}</Text>
                                    <HStack>
                                      <Text>{day.count} logins</Text>
                                      <Box
                                        height="20px"
                                        width={`${Math.max(20, (day.count / Math.max(...reportsData.timelineData.map(d => d.count))) * 200)}px`}
                                        bg="blue.500"
                                        borderRadius="md"
                                      />
                                    </HStack>
                                  </Flex>
                                ))}
                              </Box>
                              <Text fontSize="xs" color="gray.500" mt={2}>
                                Total logins: {reportsData.totalLogins || reportsData.timelineData.reduce((sum, day) => sum + day.count, 0)}
                              </Text>
                            </CardBody>
                          </Card>
                        )}

                        {/* Team Performance */}
                        {reportsData.teamStats.length > 0 && (
                          <Card>
                            <CardBody>
                              <Heading size="md" mb={4}>🏆 Team Performance</Heading>
                              <Box overflowX="auto">
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Team</Th>
                                      <Th isNumeric>Registrations</Th>
                                      <Th isNumeric>Total Logins</Th>
                                      <Th isNumeric>Media Pics</Th>
                                      <Th isNumeric>Pro Edits</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {reportsData.teamStats.map((team, index) => (
                                      <Tr key={index}>
                                        <Td fontWeight="medium">{team.team}</Td>
                                        <Td isNumeric>{team.registrations}</Td>
                                        <Td isNumeric>{team.totalLogins}</Td>
                                        <Td isNumeric>{team.mediaPics}</Td>
                                        <Td isNumeric>{team.proEdits}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>
                            </CardBody>
                          </Card>
                        )}

                        {/* Quick Actions */}
                        <Card>
                          <CardBody>
                            <Heading size="md" mb={4}>⚡ Quick Actions</Heading>
                            <Flex gap={4} flexWrap="wrap">
                              <Button leftIcon={<FaEnvelope />} colorScheme="blue" size="lg">
                                Send Mass Email
                              </Button>
                              <Button variant="outline" size="lg" onClick={fetchReportsData}>
                                Refresh Reports
                              </Button>
                            </Flex>
                          </CardBody>
                        </Card>
                      </VStack>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </>
          )}
        </Box>
      </div>
      <SalesBreakdownModal
        isOpen={salesModalOpen}
        onClose={() => setSalesModalOpen(false)}
        salesBreakdownData={salesBreakdownData}
        salesBreakdownLoading={salesBreakdownLoading}
        totalSales={reportsData.purchases?.totalSales}
      />
    </>
  );
  };
  
export default EventDashboard; 